import { normalizeText } from './dataUtils';

export function priceOf(item) {
  if (item.kind === 'catalog' && item.sized) return item.sizes[item.size];
  return item.price ?? 0;
}

export function computeSale(items, discount, payments) {
  const subtotal = items.reduce((s, it) => s + priceOf(it) * (it.qty || 1), 0);
  const discountAmount = !discount ? 0
    : discount.type === 'membership' ? subtotal
    : discount.type === 'bath-package' ? subtotal
    : discount.type === 'percent' ? subtotal * (discount.value / 100)
    : Math.min(subtotal, discount.value);
  const total = Math.max(0, subtotal - discountAmount);
  const paid = (payments || []).reduce((s, p) => s + p.amount, 0);
  const remaining = Math.max(0, total - paid);
  const isCovered = (discount?.type === 'membership' || discount?.type === 'bath-package') && subtotal > 0;
  const status = isCovered ? 'paid' : total === 0 ? 'draft' : remaining === 0 ? 'paid' : paid > 0 ? 'partial' : 'due';
  return { subtotal, discountAmount, total, paid, remaining, status };
}

export const ILS = (n) =>
  `$${(Math.round(n * 100) / 100).toLocaleString('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  })}`;

export const SALE_FILTERS = {
  ALL:     'all',
  PAID:    'paid',
  PARTIAL: 'partial',
  DUE:     'due',
};

export const PAYMENT_FILTERS = {
  ALL:      'all',
  CASH:     'Cash',
  CARD:     'Card',
  TRANSFER: 'Transfer',
};

export function filterSales(sales, filter) {
  if (filter === SALE_FILTERS.ALL) return sales;
  return sales.filter(s => s.status === filter);
}

export function filterSalesByPayment(sales, paymentFilter) {
  if (!paymentFilter || paymentFilter === PAYMENT_FILTERS.ALL) return sales;
  return sales.filter(s => (s.payments || []).some(p => p.method === paymentFilter));
}

const active = s => s.status !== 'cancelled';

// Convert a UTC ISO timestamp to local YYYY-MM-DD string
export function toLocalDateStr(isoString) {
  const d = new Date(isoString);
  return [d.getFullYear(), String(d.getMonth() + 1).padStart(2, '0'), String(d.getDate()).padStart(2, '0')].join('-');
}

export function todayRevenue(sales) {
  const today = isoToday();
  return sales
    .filter(s => active(s) && s.createdAt && toLocalDateStr(s.createdAt) === today)
    .reduce((sum, s) => sum + (s.payments || []).reduce((p, payment) => p + (payment.amount || 0), 0), 0);
}

export function todayCount(sales) {
  const today = isoToday();
  return sales.filter(s => active(s) && s.createdAt && toLocalDateStr(s.createdAt) === today).length;
}

export function outstandingBalance(sales) {
  return sales
    .filter(s => active(s) && s.status === 'partial')
    .reduce((sum, s) => sum + (s.remaining || 0), 0);
}

export function outstandingCount(sales) {
  return sales.filter(s => active(s) && (s.status === 'partial' || s.status === 'due')).length;
}

export function formatRelativeDate(isoString) {
  if (!isoString) return '';
  const d = new Date(isoString);
  if (isNaN(d)) return '';

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const target = new Date(d);
  target.setHours(0, 0, 0, 0);

  const diffDays = Math.round((today - target) / 86400000);

  if (diffDays === 0) return 'Hoy';
  if (diffDays === 1) return 'Ayer';
  if (diffDays > 1 && diffDays < 7) return `Hace ${diffDays} días`;
  return d.toLocaleDateString('es-ES', { day: 'numeric', month: 'short' });
}

export function formatFullDateTime(isoString) {
  if (!isoString) return '';
  const d = new Date(isoString);
  if (isNaN(d)) return '';
  const date = d.toLocaleDateString('es-ES', { day: 'numeric', month: 'short', year: 'numeric' });
  const time = d.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' });
  return `${date} · ${time}`;
}

export function isoToday() {
  const d = new Date();
  return [d.getFullYear(), String(d.getMonth() + 1).padStart(2, '0'), String(d.getDate()).padStart(2, '0')].join('-');
}

const METHOD_LABELS = { Cash: 'Efectivo', Card: 'Tarjeta', Bit: 'Bit', Transfer: 'Transferencia', Cashback: 'Cashback' };

export function getSaleDogs(sale) {
  if (sale?.dogs?.length > 0) return sale.dogs;
  if (sale?.dog) return [sale.dog];
  return [];
}

const CASHBACK_RATE = 0.01;

export function buildWhatsAppText(sale, payments, total, remaining) {
  const saleTitle = sale.saleNumber
    ? `#${String(sale.saleNumber).padStart(4, '0')}`
    : `#${(sale._id || sale.id)?.slice(-6).toUpperCase()}`;

  const saleDogs = getSaleDogs(sale);
  const dogName = saleDogs.map(d => d.name).join(' & ') || '—';
  const ownerName = saleDogs[0]?.owners?.[0]?.name || '';
  const when = formatFullDateTime(sale.createdAt);

  const paymentLines = (payments || []).map(p =>
    `  - ${METHOD_LABELS[p.method] || p.method}: ${ILS(p.amount)}`
  );

  const discount = sale.discount || null;
  const discountAmt = sale.discountAmount || 0;
  const subtotal = (sale.items || []).reduce((s, it) => s + priceOf(it) * (it.qty || 1), 0);

  const discountLabel = !discount ? '' :
    discount.type === 'membership'    ? '🏅 Membresía'
    : discount.type === 'bath-package' ? '🛁 Paquete de baños'
    : discount.type === 'percent'      ? `Descuento ${discount.value}%`
    : 'Descuento';

  const lines = [
    `*Happy Tails* - Recibo de venta ${saleTitle}`,
    when,
    ownerName ? `${ownerName} - ${dogName}` : dogName,
    '',
    '*Servicios:*',
    ...(sale.items || []).map(it => {
      const unit = priceOf(it);
      const line = `  - ${it.name}${it.sized && it.size ? ` (${it.size})` : ''}`;
      return it.qty > 1
        ? `${line} x${it.qty}: ${ILS(unit * (it.qty || 1))}`
        : `${line}: ${ILS(unit)}`;
    }),
    '',
    ...(discount && discountAmt > 0
      ? [
          `Subtotal: ${ILS(subtotal)}`,
          `${discountLabel}: −${ILS(discountAmt)}`,
        ]
      : []),
    `*Total: ${ILS(total)}*`,
    ...(paymentLines.length > 0 ? [`*Pagos:*`, ...paymentLines] : []),
    remaining > 0 ? `Saldo pendiente: ${ILS(remaining)}` : `Pagado`,
    ...(total > 0 && sale.earnCashback !== false ? [`Cashback ganado (1%): +$ ${(total * CASHBACK_RATE).toFixed(2)}`] : []),
    '',
    `Gracias por confiar en Happy Tails!`,
  ];

  return lines.join('\n');
}

export function isoYesterday() {
  const d = new Date();
  d.setDate(d.getDate() - 1);
  return [d.getFullYear(), String(d.getMonth() + 1).padStart(2, '0'), String(d.getDate()).padStart(2, '0')].join('-');
}

export const CATEGORY_SYNONYMS = {
  vaccine: ['vaccine', 'vaccines', 'vacuna', 'vacunas', 'dosis', 'rabia', 'dhpp', 'bordetella', 'leptospirosis', 'combo', 'v-rabies', 'v-dhpp', 'v-bordetella', 'v-lepto', 'v-combo'],
  transportation: ['transportation', 'transporte', 'traslado', 'happybus', 'happy bus', 'bus', 'transporte sencillo', 'transporte redondo'],
  grooming: ['grooming', 'estetica', 'estética', 'bano', 'baño', 'corte', 'peluqueria', 'peluquería', 'deslanado', 'higienico', 'higiénico'],
  vet: ['vet', 'veterinaria', 'veterinario', 'consulta', 'revision', 'revisión', 'sangre', 'limpieza', 'medica', 'médica'],
  daycare: ['daycare', 'guarderia', 'guardería', 'pack', 'día', 'dia'],
  hotel: ['hotel', 'hospedaje', 'habitacion', 'habitación', 'paseo'],
};

export function matchesSaleSearch(sale, query) {
  if (!query || !query.trim()) return true;
  const q = normalizeText(query.trim());
  if (!q) return true;

  // 1. Check Dog names & breeds
  const dogs = getSaleDogs(sale);
  const dogText = normalizeText(dogs.map(d => `${d.name || ''} ${d.breed || ''}`).join(' '));
  if (dogText.includes(q)) return true;

  // 2. Check Owner names & phones & emails
  const ownerText = normalizeText(
    dogs.flatMap(d => d.owners?.map(o => `${o.name || ''} ${o.phone || ''} ${o.email || ''}`) || [])
      .concat(sale.owner ? [`${sale.owner.name || ''} ${sale.owner.phone || ''} ${sale.owner.email || ''}`] : [])
      .join(' ')
  );
  if (ownerText.includes(q)) return true;

  // 3. Check Sale Number & Sale ID
  const saleIdStr = sale._id || sale.id || '';
  const saleNum = sale.saleNumber ? String(sale.saleNumber) : saleIdStr.slice(-6);
  if (normalizeText(saleNum).includes(q) || normalizeText(saleIdStr).includes(q)) return true;

  // 4. Check Items (name, description, cat, catalogId) and product/category synonyms
  const items = sale.items || [];
  for (const item of items) {
    const itemName = normalizeText(item.name);
    const itemDesc = normalizeText(item.description);
    const itemCat = normalizeText(item.cat);
    const catalogId = normalizeText(item.catalogId);

    // Direct text search in item properties
    if (itemName.includes(q) || itemDesc.includes(q) || itemCat.includes(q) || catalogId.includes(q)) {
      return true;
    }

    // Product & category synonym matching
    for (const [catKey, synonyms] of Object.entries(CATEGORY_SYNONYMS)) {
      const qMatchesCategory = synonyms.some(syn => normalizeText(syn).includes(q) || q.includes(normalizeText(syn)));
      if (qMatchesCategory) {
        const itemBelongsToCategory = itemCat === catKey || synonyms.some(syn => itemName.includes(normalizeText(syn)) || itemDesc.includes(normalizeText(syn)));
        if (itemBelongsToCategory) return true;
      }
    }
  }

  return false;
}

